import { format, parseISO } from 'date-fns';

import type { TransportDay } from '@/components/molecules/TransportSection';

type ApiTransportRow = {
	id?: string;
	date?: string;
	entryTime?: string | null;
	exitTime?: string | null;
	transportType?: string | number | null;
	location?: string | number | null;
	workStation?: { id?: number; name?: string } | null;
	phone?: string | null;
	requestiaId?: string | null;
	requestiaStatus?: string | null;
};

function normalizeDateToYyyyMmDd(dateVal: unknown): string {
	if (dateVal == null || dateVal === '') return '';
	if (typeof dateVal === 'string') {
		return dateVal.includes('T') ? dateVal.slice(0, 10) : dateVal.slice(0, 10);
	}
	try {
		return format(dateVal as Date, 'yyyy-MM-dd');
	} catch {
		return '';
	}
}

function formatTimeForInput(t: unknown): string {
	if (t == null || t === '') return '';
	const s = String(t);
	if (s.includes('T')) {
		try {
			return format(parseISO(s), 'HH:mm');
		} catch {
			return s.slice(11, 16);
		}
	}
	return s.length >= 5 ? s.slice(0, 5) : s;
}

/**
 * The API (VacationDto) sends `transportData` as an array of days with `workStation` and `phone` per day.
 * The form uses `{ transportDays, passengerInfo }`.
 */
export function normalizePersistedTransportBundle(raw: unknown): {
	transportDays: TransportDay[];
	passengerPhone: string;
} {
	const empty = { transportDays: [] as TransportDay[], passengerPhone: '' };
	if (raw == null) return empty;

	let rows: ApiTransportRow[] = [];
	let nestedPhone = '';

	if (Array.isArray(raw)) {
		rows = raw as ApiTransportRow[];
	} else if (
		typeof raw === 'object' &&
		raw !== null &&
		'transportDays' in raw
	) {
		const o = raw as {
			transportDays?: ApiTransportRow[];
			passengerInfo?: { phone?: string };
		};
		rows = o.transportDays ?? [];
		nestedPhone = o.passengerInfo?.phone?.trim() ?? '';
	} else {
		return empty;
	}

	const transportDays: TransportDay[] = rows.map((row, i) => {
		const dateStr = normalizeDateToYyyyMmDd(row.date);
		const locRaw =
			row.location != null && row.location !== ''
				? Number(row.location)
				: row.workStation?.id != null
					? Number(row.workStation.id)
					: null;
		const location = locRaw != null && !Number.isNaN(locRaw) ? locRaw : null;
		return {
			id: row.id ?? `day-${i}-${dateStr}`,
			date: dateStr,
			entryTime: formatTimeForInput(row.entryTime),
			exitTime: formatTimeForInput(row.exitTime),
			transportType: row.transportType ?? null,
			location,
			requestiaId: row.requestiaId ?? null,
			requestiaStatus: row.requestiaStatus ?? null,
		};
	});

	const phoneFromDay = rows
		.map((r) => r.phone)
		.find((p) => p && String(p).trim());
	const passengerPhone =
		(typeof phoneFromDay === 'string' ? phoneFromDay.trim() : '') ||
		nestedPhone;

	return { transportDays, passengerPhone };
}

export { normalizeDateToYyyyMmDd };

/** Converts API response to the format stored in react-hook-form. */
export function toFormTransportDataShape(
	raw: unknown,
):
	| { transportDays: TransportDay[]; passengerInfo: { phone: string } }
	| undefined {
	if (raw == null) return undefined;
	if (Array.isArray(raw) && raw.length === 0) return undefined;
	if (
		typeof raw === 'object' &&
		raw !== null &&
		'transportDays' in raw &&
		!Array.isArray(raw)
	) {
		const bundle = normalizePersistedTransportBundle(raw);
		return {
			transportDays: bundle.transportDays,
			passengerInfo: { phone: bundle.passengerPhone },
		};
	}
	const bundle = normalizePersistedTransportBundle(raw);
	if (bundle.transportDays.length === 0 && !bundle.passengerPhone) {
		return undefined;
	}
	return {
		transportDays: bundle.transportDays,
		passengerInfo: { phone: bundle.passengerPhone },
	};
}
